import { useState } from "react";
import { MdDelete } from "react-icons/md";
import { ImSpinner2 } from "react-icons/im";
import { CARD_ICONS } from "../../assets/icons";
import { useDetachPaymentMethod } from "../../api/hooks";
import { Tooltip } from "../Tooltips/Tooltip";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function PaymentRow({ payment, canDelete = true }) {
  const card = payment.card;
  const billing = payment.billing_details;

  const expMonthName = card.exp_month ? MONTHS[card.exp_month - 1] : "";

  const [isDeleting, setIsDeleting] = useState(false);
  const { mutateAsync: deletePayment } = useDetachPaymentMethod();

  const finalCanDelete = canDelete && !payment.inUse;

  const handleDelete = async () => {
    if (!finalCanDelete) return;
    try {
      setIsDeleting(true);
      await deletePayment(payment.id);
    } catch (err) {
      console.error("Failed to delete payment method:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Tooltip messaging
  const tooltipMessage = payment.inUse
    ? "This payment method is currently in use for an active or auditing shipment."
    : !canDelete
      ? "Cannot delete the last valid card on file."
      : "Delete payment method";

  return (
    <div
      className="group relative p-4 rounded-xl border border-gray-100 bg-white 
                 hover:shadow-md hover:bg-gray-50 transition-all"
    >
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">{CARD_ICONS[card.brand]}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-x-3">
            <span className="text-sm font-medium text-gray-900 truncate">
              •••• {card.last4}
            </span>

            {payment.inUse && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
             text-[10px] font-medium
             bg-blue-50 text-blue-700 border border-blue-200"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                In use
              </span>
            )}
          </div>

          <span className="text-xs text-gray-500">
            Exp: {expMonthName} {card.exp_year}
          </span>
        </div>
      </div>

      {(billing?.name ||
        billing?.address?.postal_code ||
        billing?.address?.country) && (
        <div className="mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500 space-y-0.5">
          {billing?.name && <div className="truncate">{billing.name}</div>}

          {(billing?.address?.postal_code || billing?.address?.country) && (
            <div className="truncate">
              {billing?.address?.postal_code}{" "}
              {billing?.address?.country && `(${billing.address.country})`}
            </div>
          )}
        </div>
      )}

      {/* Delete action */}
      <div className="absolute top-3 right-3 flex gap-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={handleDelete}
          disabled={isDeleting || !finalCanDelete}
          data-tooltip-id={`tooltip-${payment.id}`}
          className={`p-1.5 rounded-md transition flex items-center justify-center
            ${
              !finalCanDelete
                ? "bg-gray-200 cursor-not-allowed"
                : "bg-gray-100 hover:bg-red-50 border border-gray-200"
            }
          `}
        >
          {isDeleting ? (
            <ImSpinner2 className="h-4 w-4 text-red-600 animate-spin" />
          ) : (
            <MdDelete
              className={`h-4 w-4 text-red-600 ${
                !finalCanDelete ? "opacity-40" : ""
              }`}
            />
          )}
        </button>

        <Tooltip id={`tooltip-${payment.id}`}>{tooltipMessage}</Tooltip>
      </div>
    </div>
  );
}
